import { cn } from '../../../../lib/utils';
import { Close } from '@crabs-ai/web-icons';
import { cva } from 'class-variance-authority';

const chipVariants = cva(
  [
    'flex items-center gap-5xs transition-all cursor-pointer [&_path]:transition-all',
  ],
  {
    variants: {
      state: {
        default: '',
        disabled: 'cursor-not-allowed',
      },
      style: {
        filled: [''],
        outlined: ['border-[1px]'],
      },
      selected: {
        true: '',
        false: '',
      },
      size: {
        sm: [
          '[&_svg]:size-[16px]',
          'h-[26px] min-h-[26px] max-h-[26px]',
          'rounded-2xs',
          'px-2xs',
        ],
        md: [
          '[&>svg]:size-[20px]',
          'h-[30px] min-h-[30px] max-h-[30px]',
          'rounded-2xs',
          'px-2xs',
        ],
        lg: [
          '[&_svg]:size-[24px]',
          'h-[36px] min-h-[36px] max-h-[36px]',
          'rounded-xs',
          'px-xs',
        ],
      },
    },
    compoundVariants: [
      {
        state: 'default',
        style: 'filled',
        selected: false,
        className: [
          'bg-comp-chip-unselected-bg-default hover:bg-comp-chip-unselected-bg-hover active:bg-comp-chip-unselected-bg-active',
        ],
      },
      {
        state: 'default',
        style: 'filled',
        selected: true,
        className: [
          'bg-comp-chip-selected-bg-default hover:bg-comp-chip-selected-bg-hover active:bg-comp-chip-selected-bg-active',
        ],
      },
      {
        state: 'default',
        style: 'outlined',
        selected: false,
        className: [
          'bg-comp-chip-outlined-bg-default hover:bg-comp-chip-outlined-bg-hover active:bg-comp-chip-outlined-bg-active',
          'border-comp-chip-outlined-border-default',
          '[&_path]:fill-comp-chip-text-default',
        ],
      },
      {
        state: 'default',
        style: 'outlined',
        selected: true,
        className: [
          'bg-comp-chip-outlined-bg-default hover:bg-comp-chip-outlined-bg-hover active:bg-comp-chip-outlined-bg-active',
          'border-comp-chip-outlined-border-selected',
          '[&_path]:fill-sys-cont-neutral-default',
        ],
      },
      {
        selected: false,
        style: 'filled',
        className: [
          'text-comp-chip-text-default [&_path]:fill-comp-chip-text-default',
        ],
      },
      {
        selected: true,
        style: 'filled',
        className: [
          'text-sys-cont-inverse-light [&_path]:fill-sys-cont-inverse-light',
        ],
      },
      {
        state: 'disabled',
        style: 'filled',
        selected: false,
        className: [
          'bg-comp-chip-unselected-bg-default text-comp-chip-text-disabled',
          '[&_path]:fill-comp-chip-text-disabled',
        ],
      },
      {
        state: 'disabled',
        style: 'filled',
        selected: true,
        className: [
          'bg-sys-style-neutral-dark text-sys-cont-inverse-light',
          '[&_path]:fill-sys-cont-inverse-light',
        ],
      },
      {
        state: 'disabled',
        style: 'outlined',
        selected: false,
        className: [
          'border-comp-chip-outlined-border-default bg-comp-chip-outlined-bg-default text-comp-chip-text-disabled',
          '[&_path]:fill-comp-chip-text-disabled',
        ],
      },
      {
        state: 'disabled',
        style: 'outlined',
        selected: true,
        className: [
          'border-comp-chip-outlined-border-selected bg-comp-chip-outlined-bg-default text-sys-cont-neutral-disabled-default',
          '[&_path]:fill-sys-cont-neutral-disabled-default',
        ],
      },
    ],
    defaultVariants: {
      state: 'default',
      selected: false,
      size: 'lg',
    },
  },
);

export interface ChipProps {
  /** 칩의 상태 */
  state?: 'default' | 'disabled';
  /** 칩의 스타일 */
  style?: 'filled' | 'outlined';
  /** 칩의 크기 (sm: 26px, md: 30px, lg: 36px) */
  size?: 'sm' | 'md' | 'lg';
  /** 칩이 선택되었는지 여부 */
  selected?: boolean;
  /** 선행 노드 표시 여부 */
  isShowLeadingIcon?: boolean;
  /** 선행 노드 (아이콘 또는 Label 컴포넌트) */
  leadingIconNode?: React.ReactNode;
  /** 후행 노드 표시 여부 */
  isShowTrailingIcon?: boolean;
  /** 후행 노드 (아이콘) */
  trailingIconNode?: React.ReactNode;
  /** 칩의 레이블 노드 */
  labelNode?: React.ReactNode;
}

export default function Chip({
  ...props
}: ChipProps & React.ComponentProps<'div'>) {
  const {
    state = 'default',
    selected = false,
    isShowLeadingIcon = false,
    leadingIconNode,
    isShowTrailingIcon = false,
    trailingIconNode,
    labelNode,
    size = 'lg',
    style = 'filled',
    className,
    ...rest
  } = props;

  // 사이즈별 텍스트 스타일 매핑
  const textStyleMap = {
    sm: 'text-comp-moderate-small',
    md: 'text-comp-moderate-small',
    lg: 'text-comp-moderate-medium',
  };

  return (
    <div
      data-custom-state={state}
      data-custom-style={style}
      className={cn(
        'group',
        chipVariants({ state, selected, size, style }),
        className,
      )}
      {...rest}
    >
      {isShowLeadingIcon && (
        <div
          className={cn(
            'flex items-center shrink-0',
            // md 사이즈일 때 직접적인 svg 자식 아이콘은 20px로 고정
            size === 'md' && '[&>svg]:size-[20px]',
          )}
        >
          {leadingIconNode}
        </div>
      )}
      <div className={cn('chip-label shrink-0', textStyleMap[size])}>
        {labelNode}
      </div>
      {isShowTrailingIcon && (
        <div
          className={cn(
            'flex items-center',
            // md 사이즈일 때 직접적인 svg 자식 아이콘은 20px로 고정
            size === 'md' && '[&>svg]:size-[20px]',
          )}
        >
          {trailingIconNode || <Close className="[&_svg]:transition-all" />}
        </div>
      )}
    </div>
  );
}
